import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Edges, TransformControls} from "@react-three/drei";
import {useFrame, useThree} from "@react-three/fiber";
import * as THREE from "three";
import ModuleDetailMesh from "../module3d/ModuleDetailMesh";
import DetailCornerHandles from "../module3d/DetailCornerHandles";
import DetailPanelAxes from "../module3d/DetailPanelAxes";
import {
    applyAxesToObject,
    axesToEulerDegrees,
    cloneAxes,
    IDENTITY_AXES,
} from "../module3d/orientationAxes";
import {
    cubeLocalToWorld,
    getDominantOrthoAxis,
    getInstanceLocalCornersM,
    getSnapApproachPreview,
    normalizeModuleSizeM,
    orthoAxisFromTransformControls,
    projectLocalToOrthoAxis,
    resolveDragPose,
    snapLocalToOtherDetails,
    worldPointToCubeLocal,
} from "../module3d/snapPlacement";
import CompositionModulePriorityMarker3d from "./CompositionModulePriorityMarker3d.jsx";

const findNearestModuleCorner = (localPoint, corners, maxDistance = Infinity) => {
    let nearestCorner = null;
    let minDist = Infinity;
    for (const corner of corners) {
        const dx = localPoint.x - corner[0];
        const dy = localPoint.y - corner[1];
        const dz = localPoint.z - corner[2];
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (dist < minDist) {
            minDist = dist;
            nearestCorner = corner;
        }
    }
    if (!nearestCorner || minDist > maxDistance) return null;
    return nearestCorner;
};

const hideMultiAxisTransformHandles = (controls) => {
    if (!controls) return;
    controls.traverse((obj) => {
        const name = obj.name;
        if (name === "XY" || name === "YZ" || name === "XZ" || name === "XYZ") {
            obj.visible = false;
            obj.scale.setScalar(1e-10);
        }
    });
};

/**
 * Зелёный куб в СК модуля: (0,0,0) — угол, из него +X/+Y/+Z внутрь объёма
 * (как DetailPanelAxes и точка привязки на сцене родителя).
 */
const CompositionModuleReferenceCube = ({
    sizeM,
    selected = false,
    shellTransparent = false,
    shellVisible = true,
    loading = false,
    shellPickable = false,
    onShellSelect,
    onShellPointerDown,
    onShellContextMenu,
}) => {
    const {width, height, depth} = sizeM;
    const position = [width / 2, height / 2, depth / 2];

    if (loading) {
        return (
            <mesh
                position={position}
                userData={{module3dRole: "embedded-shell", module3dLoading: true}}
            >
                <boxGeometry args={[width, height, depth]}/>
                <meshBasicMaterial
                    color="#78909c"
                    wireframe
                    transparent
                    opacity={0.9}
                    depthWrite={false}
                />
            </mesh>
        );
    }

    return (
        <mesh
            position={position}
            userData={{module3dRole: "embedded-shell"}}
            onPointerDown={shellPickable ? onShellPointerDown : undefined}
            onClick={shellPickable ? onShellSelect : undefined}
            onContextMenu={shellPickable ? onShellContextMenu : undefined}
        >
            <boxGeometry args={[width, height, depth]}/>
            {shellVisible ? (
                <>
                    <meshStandardMaterial
                        color={shellTransparent ? "#ffffff" : (selected ? "#388e3c" : "#2e7d32")}
                        transparent
                        opacity={shellTransparent ? 0.22 : (selected ? 0.42 : 0.28)}
                        depthWrite={!shellTransparent}
                    />
                    <Edges
                        color={shellTransparent ? "#000000" : (selected ? "#f9a825" : "#1b5e20")}
                        threshold={15}
                    />
                </>
            ) : (
                <meshBasicMaterial transparent opacity={0} depthWrite={false}/>
            )}
        </mesh>
    );
};

/** Вылет жёлтого куба подсветки за габарит модуля, мм → м (с каждой стороны). */
const LIST_HOVER_HIGHLIGHT_PAD_M = 0.018;

/** Подсветка из списка шкафов: ограничивающий куб поверх деталей (виден и с фасада). */
const ModuleListHoverHighlight = ({sizeM}) => {
    const {width, height, depth} = sizeM;
    const pad = LIST_HOVER_HIGHLIGHT_PAD_M;
    const position = [width / 2, height / 2, depth / 2];

    return (
        <group userData={{module3dRole: "catalog-list-hover"}} raycast={() => null}>
            <mesh position={position} renderOrder={25} raycast={() => null}>
                <boxGeometry args={[width + pad * 2, height + pad * 2, depth + pad * 2]}/>
                <meshBasicMaterial
                    color="#FFEB3B"
                    transparent
                    opacity={0.52}
                    depthWrite={false}
                    depthTest={false}
                />
                <Edges color="#F57F17" threshold={15}/>
            </mesh>
        </group>
    );
};

const CompositionModuleChildMesh = ({
    child,
    showHoles,
    showGrooves,
    showOtherMachining,
    shellTransparent = false,
    pickable = true,
    onSelect,
    moduleListPointerHandlers = null,
}) => {
    const groupRef = useRef();

    useLayoutEffect(() => {
        if (!groupRef.current) return;
        const local = child.localPosition || {x: 0, y: 0, z: 0};
        groupRef.current.position.set(local.x, local.y, local.z);
        applyAxesToObject(groupRef.current, child.axes, child.faceFlip);
    }, [child.localPosition, child.axes, child.faceFlip]);

    return (
        <group
            ref={groupRef}
            onPointerOver={moduleListPointerHandlers?.onPointerOver}
            onPointerOut={moduleListPointerHandlers?.onPointerOut}
            onClick={moduleListPointerHandlers?.onClick}
        >
            <ModuleDetailMesh
                detailData={child.detailData}
                textureUrl={child.textureUrl}
                textureScaleMm={child.textureScaleMm}
                textureRotationDeg={child.textureRotationDeg}
                selected={false}
                showHoles={showHoles}
                showGrooves={showGrooves}
                showOtherMachining={showOtherMachining}
                shellTransparent={shellTransparent}
                renderOrder={child.sceneDrawOrder ?? 0}
                onSelect={pickable ? onSelect : undefined}
            />
        </group>
    );
};

/** Модуль композиции на сцене родителя: отрисовка, выбор, gizmo и привязка. */
const PlacedCompositionModuleGroup = ({
    inst,
    parentCompositionSizeM,
    selected = false,
    selectionActive = false,
    addingMode = false,
    snapEnabled = true,
    otherInstances = [],
    onSelect,
    onTransformEnd,
    gizmoDraggingRef,
    interactionLocked = false,
    suppressSelectionVisual = false,
    showHoles = true,
    showGrooves = true,
    showOtherMachining = true,
    shellTransparent = false,
    showGreenShell = true,
    listHoverHighlight = false,
    catalogListInteraction = false,
    onCatalogModuleListHoverStart = null,
    onCatalogModuleListHoverEnd = null,
    onCatalogModuleListClick = null,
    onSnapApproachChange = null,
    snapApproach = null,
    orthoEnabled = false,
    onSelectedRightPointerDown = null,
    onSceneContextMenu = null,
    compositionCameraView = null,
    compactPriorityMarker = false,
}) => {
    const groupRef = useRef();
    const transformControlsRef = useRef(null);
    const draggingRef = useRef(false);
    const dragSourceRef = useRef(null);
    const liveSnapPrevLocalRef = useRef(null);
    const orthoStartLocalRef = useRef(null);
    const orthoLockedAxisRef = useRef(null);
    const activeSnapNodeRef = useRef(null);
    const shellCornerDragRef = useRef(null);
    const shellDragMovedRef = useRef(false);
    const [object, setObject] = useState(null);
    const {controls: orbitControls, camera, gl} = useThree();

    const setGroupRef = useCallback((node) => {
        groupRef.current = node;
        setObject((prev) => (prev === node ? prev : node));
    }, []);

    useLayoutEffect(() => {
        if (draggingRef.current || !groupRef.current) return;
        const world = cubeLocalToWorld(inst.localPosition, parentCompositionSizeM);
        groupRef.current.position.set(world.x, world.y, world.z);
        applyAxesToObject(groupRef.current, inst.axes || IDENTITY_AXES, Boolean(inst.faceFlip));
    }, [inst.localPosition, inst.axes, inst.faceFlip, parentCompositionSizeM]);

    useFrame(() => {
        if (!orthoEnabled) return;
        hideMultiAxisTransformHandles(transformControlsRef.current);
    });

    const showSelectionChrome = selected && !suppressSelectionVisual;
    const shellPickable = !addingMode && !interactionLocked;
    // На сцене композиции детали двигаются только вместе с модулем.
    const detailPickable = false;
    const catalogDetailRaycast = catalogListInteraction && !addingMode;

    const handleCatalogModulePointerOver = useCallback((event) => {
        if (!catalogListInteraction) {
            return;
        }
        event.stopPropagation();
        onCatalogModuleListHoverStart?.(inst.row);
    }, [catalogListInteraction, inst.row, onCatalogModuleListHoverStart]);

    const handleCatalogModulePointerOut = useCallback((event) => {
        if (!catalogListInteraction) {
            return;
        }
        event.stopPropagation();
        onCatalogModuleListHoverEnd?.(inst.row);
    }, [catalogListInteraction, inst.row, onCatalogModuleListHoverEnd]);

    const handleCatalogModuleClick = useCallback((event) => {
        if (!catalogListInteraction || event.button !== 0) {
            return;
        }
        event.stopPropagation();
        onCatalogModuleListClick?.(inst.row);
    }, [catalogListInteraction, inst.row, onCatalogModuleListClick]);

    const catalogModuleListPointerHandlers = useMemo(() => (
        catalogDetailRaycast
            ? {
                onPointerOver: handleCatalogModulePointerOver,
                onPointerOut: handleCatalogModulePointerOut,
                onClick: handleCatalogModuleClick,
            }
            : null
    ), [
        catalogDetailRaycast,
        handleCatalogModulePointerOver,
        handleCatalogModulePointerOut,
        handleCatalogModuleClick,
    ]);

    const resolveOrthoAxis = useCallback((rawLocal) => {
        const start = orthoStartLocalRef.current;
        if (!start) return null;
        if (dragSourceRef.current === "gizmo") {
            const fromGizmo = orthoAxisFromTransformControls(transformControlsRef.current?.axis);
            if (fromGizmo) {
                orthoLockedAxisRef.current = fromGizmo;
                return fromGizmo;
            }
            if (orthoLockedAxisRef.current) {
                return orthoLockedAxisRef.current;
            }
        }
        return getDominantOrthoAxis(start, rawLocal);
    }, []);

    const applyOrthoToLocal = useCallback((rawLocal) => {
        const start = orthoStartLocalRef.current;
        if (!start) return rawLocal;
        return projectLocalToOrthoAxis(start, rawLocal, resolveOrthoAxis(rawLocal));
    }, [resolveOrthoAxis]);

    const commitTransform = useCallback(() => {
        const obj = groupRef.current;
        if (!obj) return;
        const axes = cloneAxes(inst.axes || IDENTITY_AXES);
        const faceFlip = Boolean(inst.faceFlip);
        let localPosition = worldPointToCubeLocal(obj.position, parentCompositionSizeM);
        if (orthoEnabled && orthoStartLocalRef.current) {
            localPosition = applyOrthoToLocal(localPosition);
        }
        const raw = {
            localPosition,
            axes,
            faceFlip,
            euler: axesToEulerDegrees(axes),
        };
        const snapped = resolveDragPose({
            ...raw,
            inst,
            others: otherInstances,
            snapEnabled,
            cubeSize: parentCompositionSizeM,
            activeNodeLocal: activeSnapNodeRef.current,
        });
        if (orthoEnabled && orthoStartLocalRef.current) {
            const axis = resolveOrthoAxis(localPosition);
            snapped.localPosition = projectLocalToOrthoAxis(
                orthoStartLocalRef.current,
                snapped.localPosition,
                axis,
            );
        }
        const world = cubeLocalToWorld(snapped.localPosition, parentCompositionSizeM);
        obj.position.set(world.x, world.y, world.z);
        applyAxesToObject(obj, snapped.axes, faceFlip);
        onTransformEnd?.(inst.instanceId, {
            localPosition: snapped.localPosition,
            axes: snapped.axes,
            faceFlip,
            euler: snapped.euler || raw.euler,
        });
    }, [
        applyOrthoToLocal,
        inst,
        onTransformEnd,
        otherInstances,
        orthoEnabled,
        parentCompositionSizeM,
        resolveOrthoAxis,
        snapEnabled,
    ]);

    const applyLiveSnap = useCallback(() => {
        if (!groupRef.current) return;
        const obj = groupRef.current;
        let local = worldPointToCubeLocal(obj.position, parentCompositionSizeM);
        if (orthoEnabled && orthoStartLocalRef.current) {
            const constrained = applyOrthoToLocal(local);
            if (
                constrained.x !== local.x
                || constrained.y !== local.y
                || constrained.z !== local.z
            ) {
                const world = cubeLocalToWorld(constrained, parentCompositionSizeM);
                obj.position.set(world.x, world.y, world.z);
                local = constrained;
            }
        }
        if (snapEnabled) {
            const preview = getSnapApproachPreview({
                inst,
                localPosition: local,
                axes: cloneAxes(inst.axes || IDENTITY_AXES),
                others: otherInstances,
                cubeSize: parentCompositionSizeM,
                activeNodeLocal: activeSnapNodeRef.current,
            });
            onSnapApproachChange?.((prev) => {
                if (!preview && !prev) return prev;
                return preview;
            });
        }
        if (!snapEnabled) return;
        let snapped = snapLocalToOtherDetails({
            inst,
            localPosition: local,
            axes: cloneAxes(inst.axes || IDENTITY_AXES),
            others: otherInstances,
            cubeSize: parentCompositionSizeM,
            mode: "live",
            previousLocalPosition: liveSnapPrevLocalRef.current,
            activeNodeLocal: activeSnapNodeRef.current,
        });
        if (orthoEnabled && orthoStartLocalRef.current) {
            const axis = resolveOrthoAxis(local);
            snapped = projectLocalToOrthoAxis(orthoStartLocalRef.current, snapped, axis);
        }
        liveSnapPrevLocalRef.current = {...local};
        if (
            snapped.x === local.x
            && snapped.y === local.y
            && snapped.z === local.z
        ) {
            return;
        }
        const world = cubeLocalToWorld(snapped, parentCompositionSizeM);
        obj.position.set(world.x, world.y, world.z);
        liveSnapPrevLocalRef.current = {...snapped};
    }, [
        applyOrthoToLocal,
        inst,
        orthoEnabled,
        otherInstances,
        parentCompositionSizeM,
        resolveOrthoAxis,
        snapEnabled,
        onSnapApproachChange,
    ]);

    const beginDrag = useCallback((source) => {
        const startLocal = groupRef.current
            ? worldPointToCubeLocal(groupRef.current.position, parentCompositionSizeM)
            : null;
        draggingRef.current = true;
        dragSourceRef.current = source;
        liveSnapPrevLocalRef.current = startLocal;
        orthoStartLocalRef.current = startLocal ? {...startLocal} : null;
        orthoLockedAxisRef.current = source === "gizmo"
            ? orthoAxisFromTransformControls(transformControlsRef.current?.axis)
            : null;
        if (gizmoDraggingRef) gizmoDraggingRef.current = true;
        if (orbitControls) orbitControls.enabled = false;
    }, [gizmoDraggingRef, orbitControls, parentCompositionSizeM]);

    const endDrag = useCallback(() => {
        draggingRef.current = false;
        liveSnapPrevLocalRef.current = null;
        onSnapApproachChange?.(null);
        if (orbitControls) orbitControls.enabled = true;
        commitTransform();
        activeSnapNodeRef.current = null;
        orthoStartLocalRef.current = null;
        orthoLockedAxisRef.current = null;
        dragSourceRef.current = null;
        window.setTimeout(() => {
            if (gizmoDraggingRef) gizmoDraggingRef.current = false;
        }, 0);
    }, [commitTransform, gizmoDraggingRef, onSnapApproachChange, orbitControls]);

    const handleSelect = useCallback((event) => {
        event.stopPropagation();
        if (addingMode) return;
        if (gizmoDraggingRef?.current) return;
        if (shellDragMovedRef.current) {
            shellDragMovedRef.current = false;
            return;
        }
        onSelect?.(inst.instanceId);
    }, [addingMode, gizmoDraggingRef, inst.instanceId, onSelect]);

    const handleSceneContextMenu = useCallback((event) => {
        event.stopPropagation();
        const nativeEvent = event.nativeEvent ?? event;
        nativeEvent.preventDefault?.();
        if (!shellPickable || addingMode) return;
        if (!selected) {
            onSelect?.(inst.instanceId);
        }
        onSceneContextMenu?.(inst.instanceId, nativeEvent);
    }, [addingMode, inst.instanceId, onSceneContextMenu, onSelect, selected, shellPickable]);

    const startShellCornerDrag = useCallback((event, localCorner) => {
        const obj = groupRef.current;
        if (!obj) return;

        const camDir = new THREE.Vector3();
        camera.getWorldDirection(camDir);
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, event.point);

        shellCornerDragRef.current = {
            plane,
            startGroup: obj.position.clone(),
            startHit: event.point.clone(),
            localPosition: [...localCorner],
            ortho: Boolean(orthoEnabled),
        };
        shellDragMovedRef.current = false;
        activeSnapNodeRef.current = localCorner;
        beginDrag("corner");
    }, [beginDrag, camera, orthoEnabled]);

    const handleShellPointerDown = useCallback((event) => {
        if (!shellPickable || addingMode) return;
        if (event.button === 2) {
            event.stopPropagation();
            if (!selected) {
                onSelect?.(inst.instanceId);
            }
            onSelectedRightPointerDown?.(inst.instanceId);
            return;
        }
        if (event.button !== 0) return;
        event.stopPropagation();
        if (gizmoDraggingRef?.current) return;

        const obj = groupRef.current;
        if (!obj) return;

        const localPoint = obj.worldToLocal(event.point.clone());
        const corners = getInstanceLocalCornersM(inst);
        const {width, height, depth} = normalizeModuleSizeM(inst.moduleSizeM);
        const minSpan = Math.min(width, height, depth);

        // Выбранный модуль: тянем за любую точку оболочки (ближайший угол — для snap).
        // Невыбранный: захват у угла сразу выделяет и двигает.
        const maxDistance = showSelectionChrome
            ? Infinity
            : minSpan * 0.28;
        const snapCorner = findNearestModuleCorner(localPoint, corners, maxDistance);
        if (!snapCorner) return;

        if (!selected) {
            onSelect?.(inst.instanceId);
        }
        startShellCornerDrag(event, snapCorner);
    }, [
        addingMode,
        gizmoDraggingRef,
        inst,
        onSelect,
        selected,
        shellPickable,
        showSelectionChrome,
        startShellCornerDrag,
        onSelectedRightPointerDown,
    ]);

    useEffect(() => {
        if (!shellPickable) {
            shellCornerDragRef.current = null;
            return undefined;
        }

        const onMove = (event) => {
            const drag = shellCornerDragRef.current;
            const obj = groupRef.current;
            if (!drag || !obj) return;

            shellDragMovedRef.current = true;

            const rect = gl.domElement.getBoundingClientRect();
            const ndc = new THREE.Vector2(
                ((event.clientX - rect.left) / rect.width) * 2 - 1,
                -((event.clientY - rect.top) / rect.height) * 2 + 1,
            );
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(ndc, camera);
            const hit = new THREE.Vector3();
            if (!raycaster.ray.intersectPlane(drag.plane, hit)) return;

            const freeDelta = hit.sub(drag.startHit);
            if (drag.ortho) {
                const ax = Math.abs(freeDelta.x);
                const ay = Math.abs(freeDelta.y);
                const az = Math.abs(freeDelta.z);
                if (ax >= ay && ax >= az) {
                    obj.position.set(
                        drag.startGroup.x + freeDelta.x,
                        drag.startGroup.y,
                        drag.startGroup.z,
                    );
                } else if (ay >= ax && ay >= az) {
                    obj.position.set(
                        drag.startGroup.x,
                        drag.startGroup.y + freeDelta.y,
                        drag.startGroup.z,
                    );
                } else {
                    obj.position.set(
                        drag.startGroup.x,
                        drag.startGroup.y,
                        drag.startGroup.z + freeDelta.z,
                    );
                }
            } else {
                obj.position.copy(drag.startGroup).add(freeDelta);
            }
            applyLiveSnap();
        };

        const onUp = () => {
            const drag = shellCornerDragRef.current;
            if (!drag) return;
            shellCornerDragRef.current = null;
            activeSnapNodeRef.current = drag.localPosition;
            endDrag();
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
        };
    }, [applyLiveSnap, camera, endDrag, gl, shellPickable]);

    useLayoutEffect(() => {
        const root = groupRef.current;
        if (!root) return undefined;
        const touched = [];
        root.traverse((obj) => {
            if (obj.userData?.module3dRole === "helper" || obj.parent?.userData?.module3dRole === "helper") {
                obj.raycast = () => null;
                return;
            }
            if (obj.userData?.module3dRole === "corner-handle") return;
            if (obj.parent?.userData?.module3dRole === "embedded-shell") {
                obj.raycast = () => null;
                return;
            }
            if (obj.userData?.module3dRole === "embedded-shell") {
                if (shellPickable) {
                    if (obj.userData.__module3dRaycastSaved) {
                        obj.raycast = obj.userData.__module3dRaycastSaved;
                        delete obj.userData.__module3dRaycastSaved;
                    }
                } else if (!obj.userData.__module3dRaycastSaved) {
                    obj.userData.__module3dRaycastSaved = obj.raycast;
                    obj.raycast = () => null;
                    touched.push(obj);
                }
                return;
            }
            if (!obj.isMesh && !obj.isLine && !obj.isLineSegments) return;
            const detailRaycastEnabled = detailPickable || catalogDetailRaycast;
            if (!detailRaycastEnabled) {
                if (!obj.userData.__module3dRaycastSaved) {
                    obj.userData.__module3dRaycastSaved = obj.raycast;
                    obj.raycast = () => null;
                    touched.push(obj);
                }
            } else if (obj.userData.__module3dRaycastSaved) {
                obj.raycast = obj.userData.__module3dRaycastSaved;
                delete obj.userData.__module3dRaycastSaved;
            }
        });
        return () => {
            touched.forEach((obj) => {
                if (obj.userData.__module3dRaycastSaved) {
                    obj.raycast = obj.userData.__module3dRaycastSaved;
                    delete obj.userData.__module3dRaycastSaved;
                }
            });
        };
    }, [
        shellPickable,
        detailPickable,
        catalogDetailRaycast,
        object,
        showHoles,
        showGrooves,
        showOtherMachining,
        inst.children,
    ]);

    return (
        <>
            <group
                ref={setGroupRef}
                userData={{
                    module3dRole: "composition-scene-module",
                    instanceId: inst.instanceId,
                    exportName: inst.label,
                }}
                onPointerDown={shellPickable ? (event) => {
                    event.stopPropagation();
                    if (event.button === 2 && !addingMode) {
                        if (!selected) {
                            onSelect?.(inst.instanceId);
                        }
                        onSelectedRightPointerDown?.(inst.instanceId);
                    }
                } : undefined}
                onContextMenu={shellPickable ? handleSceneContextMenu : undefined}
            >
                <CompositionModuleReferenceCube
                    sizeM={inst.moduleSizeM}
                    selected={showSelectionChrome}
                    shellTransparent={shellTransparent}
                    shellVisible={showGreenShell && !listHoverHighlight}
                    loading={false}
                    shellPickable={shellPickable}
                    onShellSelect={handleSelect}
                    onShellPointerDown={handleShellPointerDown}
                    onShellContextMenu={handleSceneContextMenu}
                />
                {showGreenShell && !addingMode && !listHoverHighlight && (
                    <DetailPanelAxes
                        sizeM={inst.moduleSizeM}
                        anchorAtModuleCorner
                        worldAlignedInParent
                    />
                )}
                {(inst.children || []).map((child) => (
                    <CompositionModuleChildMesh
                        key={child.instanceId}
                        child={child}
                        showHoles={showHoles}
                        showGrooves={showGrooves}
                        showOtherMachining={showOtherMachining}
                        shellTransparent={shellTransparent}
                        pickable={detailPickable}
                        onSelect={handleSelect}
                        moduleListPointerHandlers={catalogModuleListPointerHandlers}
                    />
                ))}
                {listHoverHighlight && !addingMode ? (
                    <ModuleListHoverHighlight sizeM={inst.moduleSizeM}/>
                ) : null}
                {!inst.hidden && (
                    <CompositionModulePriorityMarker3d
                        row={inst.row}
                        moduleSizeM={inst.moduleSizeM}
                        axes={inst.axes}
                        faceFlip={inst.faceFlip}
                        cameraView={compositionCameraView}
                        compact={compactPriorityMarker}
                    />
                )}
                {showSelectionChrome && !addingMode && !interactionLocked && (
                    <DetailCornerHandles
                        inst={inst}
                        enabled
                        renderOnTop
                        groupRef={groupRef}
                        orthoEnabled={orthoEnabled}
                        onDragStart={(localPosition) => {
                            activeSnapNodeRef.current = localPosition;
                            beginDrag("corner");
                        }}
                        onDrag={applyLiveSnap}
                        onDragEnd={(localPosition) => {
                            activeSnapNodeRef.current = localPosition;
                            endDrag();
                        }}
                        shrinkScale={
                            snapApproach?.sourceInstanceId === inst.instanceId
                                ? snapApproach.scale
                                : 1
                        }
                        shrinkLocal={
                            snapApproach?.sourceInstanceId === inst.instanceId
                                ? snapApproach.sourceLocal
                                : null
                        }
                    />
                )}
            </group>
            {showSelectionChrome && object && !addingMode && !interactionLocked && (
                <TransformControls
                    ref={transformControlsRef}
                    object={object}
                    mode="translate"
                    space="world"
                    size={0.85}
                    onMouseDown={(event) => {
                        event?.stopPropagation?.();
                        activeSnapNodeRef.current = [0, 0, 0];
                        beginDrag("gizmo");
                    }}
                    onObjectChange={applyLiveSnap}
                    onMouseUp={(event) => {
                        event?.stopPropagation?.();
                        endDrag();
                    }}
                />
            )}
        </>
    );
};

export default PlacedCompositionModuleGroup;
